'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useNodeInputColumns, type InputColumn } from '../../../hooks/useNodeInputs';
import { ConditionBuilder, type Condition } from '../ConditionBuilder';
import type { DataType } from '../../../types';

interface FilterConfigProps {
  nodeId: string;
  config: {
    conditions?: Condition[];
  };
}

const INFERRED_SOURCE_ID = '__inferred_from_params__';

function valueTypeToDataType(valueType: string | undefined): DataType {
  switch (valueType) {
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'time':
      return 'datetime';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}

/** 当上游尚未注册 schema（如刚应用 AI 工作流）时，仍把条件里已引用的列名加入下拉选项 */
function mergeInputColumnsWithConditions(
  inputColumns: InputColumn[] | undefined,
  conditions: Condition[] | undefined,
  inferredGroupName: string
): InputColumn[] | undefined {
  const base = inputColumns ?? [];
  const seen = new Set(base.map((c) => c.column.name));
  const extras: InputColumn[] = [];
  for (const cond of conditions ?? []) {
    const name = cond.column?.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    extras.push({
      column: {
        name,
        type: valueTypeToDataType(cond.valueType),
        nullable: true,
        index: base.length + extras.length,
      },
      source: {
        nodeId: INFERRED_SOURCE_ID,
        nodeType: 'inferred',
        nodeName: inferredGroupName,
      },
    });
  }
  if (base.length === 0 && extras.length === 0) return undefined;
  if (extras.length === 0) return base.length > 0 ? base : undefined;
  return [...base, ...extras];
}

export function FilterConfig({ nodeId, config }: FilterConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const inputColumns = useNodeInputColumns(nodeId);

  const [conditions, setConditions] = useState<Condition[]>(
    config.conditions || [{ column: '', operator: 'eq', value: '', valueType: 'string', logic: 'AND' }]
  );

  // 当 config.conditions 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.conditions && config.conditions.length > 0) {
      setConditions(config.conditions);
    }
  }, [config.conditions]);

  const columnOptions = useMemo(
    () =>
      mergeInputColumnsWithConditions(
        inputColumns,
        conditions,
        t('nodes.config.filter.inferredFromSaved'),
      ),
    [inputColumns, conditions, t],
  );

  const handleChange = useCallback(
    (next: Condition[]) => {
      setConditions(next);
      updateNodeConfig(nodeId, {
        type: 'filter',
        params: { conditions: next },
      });
    },
    [nodeId, updateNodeConfig],
  );

  return (
    <ConditionBuilder
      conditions={conditions}
      onChange={handleChange}
      columns={columnOptions}
      showLogic={true}
      showTimeGranularity={true}
      title={t('nodes.config.filter.conditions')}
    />
  );
}
